import { Injectable } from '@angular/core';

import {
  BarcodeCheckInResult,
  BarcodeCheckOutResult,
  CheckIn,
  CheckInFilters,
  CheckInStats,
  FailedCheckIn,
  InFacilityCheckIn,
  InFacilityCheckInListResponse
} from '../../models/check-in';

import { ListResponse } from '../../models/list-response';
import { ApiService } from '../api/api.service';
import { AccountService } from '../account/account.service';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CheckInService {
  /**
   * A service providing access to check-in-related functions.
   */

  constructor(
    private apiService: ApiService,
    private accountService: AccountService
  ) {}

  getCheckIns(pageNumber?: number,
              pageSize?: number,
              filters?: CheckInFilters,
              isOperational?: boolean): Promise<ListResponse<CheckIn>> {
    return this.apiService.getCollection<CheckIn, CheckInFilters>(
      'check-ins/',
      pageNumber,
      pageSize,
      filters,
      isOperational
    );
  }

  getCheckIn(id: number): Promise<CheckIn> {
    return this.apiService.getItem<CheckIn>(
      'check-ins/',
      id
    );
  }

  getCheckInStats(locationId: number | null,
                  refreshCache: boolean): Promise<CheckInStats> {
    let params = new HttpParams();
    if (locationId) {
      params = params.set('location_id', locationId.toString());
    }
    if (refreshCache) {
      params = params.set('refresh_cache', 'true');
    }

    return this.apiService.getFromAPI<CheckInStats>(
      'gym-endpoints/check-in-stats/',
      params
    );
  }

  getFailedCheckIns(locationId?: number): Promise<FailedCheckIn[]> {
    let params = new HttpParams();
    if (locationId) {
      params = params.set('location_id', locationId.toString());
    }

    return this.apiService.getFromAPI<FailedCheckIn[]>(
      'gym-endpoints/failed-check-ins/',
      params
    );
  }

  getInFacilityCustomers(locationId: number | null,
                         refreshCache: boolean): Promise<InFacilityCheckInListResponse> {
    let params = new HttpParams();
    if (locationId) {
      params = params.set('location_id', locationId.toString());
    }
    if (refreshCache) {
      params = params.set('refresh_cache', 'true');
    }

    return this.apiService.getFromAPI<InFacilityCheckInListResponse>(
      'gym-endpoints/customers-in-facility/',
      params
    );
  }

  /**
   * Check in a customer using their barcode.
   *
   * @param barcode - The barcode string.
   */
  checkInCustomerViaBarcode(barcode: string): Promise<BarcodeCheckInResult> {
    const postArguments: any = {};
    if (this.accountService.getDefaultLocation()) {
      postArguments.location_id = this.accountService.getDefaultLocation().id;
    }

    return this.apiService.postToAPI<BarcodeCheckInResult>(
      'gym-endpoints/barcode-check-in/' + barcode + '/',
      postArguments
    );
  }

  /**
   * Check in a customer using their barcode.
   *
   * @param barcode - The barcode string.
   */
  checkOutCustomerViaBarcode(barcode: string): Promise<BarcodeCheckOutResult> {
    return this.apiService.postToAPI<BarcodeCheckOutResult>(
      'gym-endpoints/barcode-check-out/' + barcode + '/',
      {}
    );
  }

  /**
   * Check out a previously-checked-in customer using their check-in ID.
   *
   * @param checkInId - The ID of the check-in object to check out.
   */
  checkOutCustomerViaCheckInId(checkInId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/customer-check-out/' + checkInId.toString() + '/',
      {}
    ).then();
  }

  /**
   * Convert an existing free entry check-in to use one of the customer's entry
   * methods.
   */
  convertFreeEntryToCheckIn(checkInId: number, bypassAlerts: boolean): Promise<void> {
    const postArguments: any = {};
    if (bypassAlerts) {
      postArguments.bypass_alerts = 'true';
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/convert-free-entry-check-in/' + checkInId.toString() + '/',
      postArguments
    ).then();
  }

  /**
   * Change the location of an existing check-in.
   */
  editCheckInLocation(checkInId: number, locationId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-check-in-location/' + checkInId.toString() + '/' + locationId.toString() + '/',
      {}
    ).then();
  }
}
